import type { ComputedCommandsSection, PaletteDef, PaletteGroupDef } from '../types';
import { DEFAULT_COMMANDS_PRIORITY, filterAndSortCommands } from './util';

type ComputeCommandsGroupOptions = {
    /**
     * Filter the palette.
     * With the new menu, it's done in Menu component.
     * @default false
     */
    filter?: boolean;
};

/**
 * Compute the commands for a group during a render protected by a Suspense boundary.
 *
 * Suspense signals thrown while resolving the group's commands are allowed to propagate to the
 * nearest boundary. This is used by the palette's per-group readers before the resolved section is
 * handed to the strict Menu tree scanner.
 */
export function readCommandsGroup(
    palette: PaletteDef,
    group: PaletteGroupDef,
    options?: ComputeCommandsGroupOptions
): ComputedCommandsSection | undefined {
    const commands =
        typeof group.commands === 'function' ? group.commands(palette.query.text) : group.commands;

    if (options?.filter) {
        // The group is considered static when it uses a function to compute commands
        // AND the function requires the `palette` argument.
        const isStaticGroup = typeof group.commands !== 'function' || group.commands.length === 0;

        // If the list is static, we do our own filtering
        if (isStaticGroup) {
            const { commands: data, priority } = filterAndSortCommands(
                palette.query.text,
                commands
            );

            return {
                group,
                priority,
                data,
            };
        }
    }

    return {
        group,
        priority: DEFAULT_COMMANDS_PRIORITY,
        data: commands,
    };
}
